// Google's satellite imagery (the Map Tiles API), apart from Leaflet so a page
// that only draws a tile onto a canvas (reports/deductionsTask.ts) does not
// load the map library. The 2D tiles need a session token, asked for once per
// browser and kept until it runs out (about two weeks).

export const TILE_BASE = 'https://tile.googleapis.com'
const SESSION_STORE = 'dp.map.googleSession'

interface Session {
  session: string
  // Seconds since the epoch, as Google gives it.
  expiry: number
}

let pending: Promise<Session | null> | null = null

export const store = {
  get(k: string): string | null {
    try {
      return localStorage.getItem(k)
    } catch {
      return null
    }
  },
  set(k: string, v: string) {
    try {
      localStorage.setItem(k, v)
    } catch {
      /* private window: not kept */
    }
  },
}

function keptSession(): Session | null {
  try {
    const s = JSON.parse(store.get(SESSION_STORE) || 'null') as Session | null
    // An hour's margin, so a map left open does not outlive its token.
    return s && typeof s.session === 'string' && Number(s.expiry) * 1000 > Date.now() + 3600_000 ? s : null
  } catch {
    return null
  }
}

export function googleSession(key: string): Promise<Session | null> {
  const kept = keptSession()
  if (kept) return Promise.resolve(kept)
  pending ??= fetch(`${TILE_BASE}/v1/createSession?key=${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ mapType: 'satellite', language: 'en-AU', region: 'AU' }),
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(`createSession ${r.status}`)
      const s = (await r.json()) as Session
      store.set(SESSION_STORE, JSON.stringify({ session: s.session, expiry: Number(s.expiry) }))
      return s
    })
    .catch((err) => {
      console.warn(`Satellite imagery is not available: ${err?.message || err}`)
      pending = null
      return null
    })
  return pending
}
